import {
  SkyAccordion,
  SkyAccordionItem,
  SkyBadge,
  SkyCard,
  SkyLink,
  SkyMessageBar,
  SkyProgressBar,
  SkySkeleton,
  SkySkeletonItem,
  SkyStepper,
  SkyText,
  type SkyBadgeColor,
  type SkyStepItem,
} from "@skypointcloud/skypoint-ui";
import {
  useLayoutEffect,
  useMemo,
  useRef,
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
} from "react";
import { Link } from "wouter";
import type { RunStatus } from "@shared/types/runs";
import { runStatusPresentation } from "@/components/runs/runFormatting";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useNow } from "@/hooks/use-now";
import { absolutePath } from "@/lib/routes";
import { CancelIcon } from "@/lib/icons";
import {
  activityAnnouncement,
  activityLine,
  activityProgress,
  isActivityLive,
  LIVENESS_TICK_MS,
  livenessLine,
  showsLiveness,
  namesItsConnection,
  type ActivityStep,
  type ImportDensity,
  type RunActivity,
  type RunActivityPhase,
} from "@/lib/runs/runActivity";

/**
 * An import's progress where the import was started (Session 10, P12; ux-s10.md §2).
 *
 * COMPOSED from `SkyBadge`, `SkyProgressBar`, `SkyText`, `SkyStepper`, `SkyLink`, `SkyCard` and
 * `SkySkeleton`, in three densities that read one `RunActivity`, so the list row, the editor
 * panel and the run page can never say different things about the same import:
 *
 * - `row`: a status badge, a small bar (indeterminate while queued), one line
 *   ("Step 2 of 3 · Loading · 7 of 40 tables") and "View", named "View import for {connection}".
 * - `panel`: "Import in progress" with the bar, the sentence, a vertical stepper of this
 *   connection's steps and the tables collapsed under "Tables (n)"; once over, the outcome line.
 *   It never offers Retry: retrying is a decision about a run, taken on the run's own page.
 * - `page`: the line, the bar and the run's whole timeline, for the run page (P11).
 *
 * Accessibility: the bar's `label` carries the text; one persistent polite line per strip,
 * whose text changes only on a step or phase change, never per table. On the list only the row
 * started from this tab announces (SF13); the others update silently. Focus never moves.
 */

const PHASE_BADGE: Record<
  Exclude<RunActivityPhase, "loading" | "unavailable" | "never">,
  RunStatus
> = {
  queued: "queued",
  running: "running",
  succeeded: "succeeded",
  partial: "partially_succeeded",
  failed: "failed",
  cancelled: "cancelled",
};

export function badgeOf(phase: RunActivityPhase): { label: string; color: SkyBadgeColor } | null {
  if (phase === "loading" || phase === "unavailable") return null;
  // One verb on a connection surface: "import" (the row line beside it reads "Not imported yet").
  if (phase === "never") return { label: "Not imported", color: "subtle" };
  const presentation = runStatusPresentation(PHASE_BADGE[phase]);
  return { label: presentation.label, color: presentation.color };
}

interface StepperModel {
  items: SkyStepItem[];
  active: number;
  /** #1089: each step's accessible name, "Step 1: Extract from orders, Cancelled". */
  names: string[];
}

function stepItems(steps: readonly ActivityStep[]): StepperModel {
  let active = -1;
  // A run of several connections names each connection's steps; one connection needs no name.
  const connections = new Set(steps.flatMap((step) => (step.dataflowId ? [step.dataflowId] : [])));
  const named = connections.size > 1;
  const items = steps.map((step, index): SkyStepItem => {
    const label =
      named && step.dataflowName && step.dataflowId && !namesItsConnection(step)
        ? `${step.label} (${step.dataflowName})`
        : step.label;
    if (step.status === "running") {
      if (active === -1) active = index;
      return { label, description: step.description };
    }
    // #1201: a cancelled step stopped short and did not fail, so it is drawn neutral with the
    // stop sign the run's Cancel uses, never the failure's red cross.
    if (step.status === "cancelled") {
      return { label, description: step.description, status: "pending", icon: CancelIcon };
    }
    return { label, description: step.description, status: stepperStatus(step.status) };
  });
  const names = items.map(
    (item, index) => `Step ${index + 1}: ${String(item.label)}, ${steps[index]?.statusWord ?? ""}`,
  );
  return { items, active, names };
}

/**
 * Only a succeeded step is "completed" (the check icon) and only a failed one "error" (the red
 * cross). A cancelled step is neutral with a stop icon (#1201, `stepItems`), and a skipped step
 * never ran, so it stays "pending"; each keeps its own word in the description and, #1089, in its
 * accessible name (`NamedStepper`).
 */
function stepperStatus(status: ActivityStep["status"]): NonNullable<SkyStepItem["status"]> {
  switch (status) {
    case "succeeded":
      return "completed";
    case "failed":
      return "error";
    default:
      return "pending";
  }
}

/**
 * `SkyStepper` with each step's accessible name in the words the screen shows (#1089). The
 * library names a step's number button "Step 1: <label>" plus its own state word, "(completed)",
 * "(current)" or "(error)", so a cancelled step was announced as an error while the screen read
 * Cancelled. The library takes no name of ours, so after each render the name is set to
 * "Step 1: <label>, <shown word>", as `QuietMessageBar` corrects the bar's role. The button
 * keeps an `aria-label`, which the library's arrow-key handling selects on.
 */
function NamedStepper({
  stepper,
  ...props
}: Omit<ComponentPropsWithoutRef<typeof SkyStepper>, "steps" | "activeStep"> & {
  stepper: StepperModel;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const buttons = Array.from(root.querySelectorAll<HTMLButtonElement>("button[aria-label]"));
    stepper.names.forEach((name, index) => {
      const prefix = `Step ${index + 1}: `;
      const button = buttons.find((each) => each.getAttribute("aria-label")?.startsWith(prefix));
      if (button && button.getAttribute("aria-label") !== name) {
        button.setAttribute("aria-label", name);
      }
    });
  });
  return <SkyStepper ref={ref} steps={stepper.items} activeStep={stepper.active} {...props} />;
}

export interface ImportProgressProps {
  activity: RunActivity;
  density: ImportDensity;
  /** The connection (or schedule) the import belongs to, for the bar and the link names. */
  connectionName: string;
  /**
   * Whether this strip owns a polite live line. Default: panel yes, row and page no. No list
   * row passes true: the connections list speaks through its one list-level line,
   * `ConnectionsImportLiveLine`, outside the grid (P12 lead fix round, S1). The page
   * density is silent by default (P12 lead fix round, nit 1): the strip's own line reads
   * `activityAnnouncement` directly, without `useImportAnnouncement`'s arming and latching, so
   * a run page that wants a live line mounts one fed by that hook instead.
   */
  announce?: boolean;
  /** Panel: the tables grid, collapsed under "Tables (n)". */
  tables?: ReactNode;
  tableCount?: number;
  /** Panel: an action for the never-imported state ("Import now" for a writer). */
  neverAction?: ReactNode;
  /**
   * Panel: only the live part (bar, sentence, cause, stepper, live line), for a host card that
   * already has its own title, badge and link (the editor's `LastRunPanel`).
   */
  bare?: boolean;
  testId?: string;
  now?: Date;
}

/**
 * One strip's polite live line. A host whose strip changes shape (the editor, where the panel
 * appears and changes branch as an import starts and ends) mounts this ONCE, outside what it
 * swaps, and passes `announce={false}` to the strip, so the region is in the page before its
 * text changes and every change (queued, a step, finished, stopped) is announced.
 */
export function LiveLine({ text, testId }: { text: string; testId: string }) {
  return (
    <span className="sr-only" aria-live="polite" aria-atomic="true" data-testid={testId}>
      {text}
    </span>
  );
}

/**
 * The failure's cause, shown but not announced: the library hard-codes `role="alert"` on an
 * error bar, which would interrupt, and the strip's polite live line already says the import
 * stopped (ux-s10.md §2: one polite line per strip). The same pattern as P13a's notice bars.
 */
function QuietMessageBar(props: ComponentPropsWithoutRef<typeof SkyMessageBar>) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    ref.current?.removeAttribute("role");
    ref.current?.removeAttribute("aria-live");
  });
  return <SkyMessageBar {...props} ref={ref} />;
}

export function ViewLink({
  runId,
  connectionName,
  children,
  testId,
}: {
  runId: string;
  connectionName: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <SkyLink
      as={Link}
      href={absolutePath(`/runs/${runId}`)}
      size="small"
      aria-label={`View import for ${connectionName}`}
      testId={testId}
    >
      {children}
    </SkyLink>
  );
}

/** The progress bar's accessible name: queued, the step, or the import alone. */
function barLabelOf(activity: RunActivity, connectionName: string): string {
  if (activity.phase === "queued") return `${connectionName} import: queued`;
  if (activity.stepIndex !== null && activity.stepTotal !== null) {
    return `${connectionName} import: step ${activity.stepIndex} of ${activity.stepTotal}`;
  }
  return `${connectionName} import`;
}

/** What every density's strip is drawn from. */
interface StripView {
  activity: RunActivity;
  testId: string;
  connectionName: string;
  badge: ReturnType<typeof badgeOf>;
  live: boolean;
  progress: number | undefined;
  barLabel: string;
  line: string;
  speaks: boolean;
  announcement: string;
  stepper: StepperModel;
  /** #2389: the running extract's "Still reading from ..." line; null otherwise. */
  liveness: string | null;
}

/**
 * #2389: the line under the step while the extract runs, so the strip is never dark. Not a live
 * region: its time moves every tick, and the strip's one polite line speaks for the step.
 */
function LivenessText({ text, testId }: { text: string | null; testId: string }) {
  if (!text) return null;
  return (
    <SkyText size={200} color="muted" block testId={`${testId}-liveness`}>
      {text}
    </SkyText>
  );
}

/** A list row: the badge and View, the bar while live, the line. */
function rowView(view: StripView): ReactElement {
  const {
    activity,
    testId,
    connectionName,
    badge,
    live,
    progress,
    barLabel,
    line,
    speaks,
    announcement,
  } = view;
  return (
    <div className="flex min-w-0 flex-col items-start gap-1" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        {badge ? (
          <SkyBadge appearance="tint" color={badge.color} testId={`${testId}-badge`}>
            {badge.label}
          </SkyBadge>
        ) : null}
        {activity.runId ? (
          <ViewLink
            runId={activity.runId}
            connectionName={connectionName}
            testId={`${testId}-view`}
          >
            View
          </ViewLink>
        ) : null}
      </div>
      {live ? (
        <SkyProgressBar
          thickness="small"
          value={progress}
          label={barLabel}
          className="w-40 max-w-full"
          testId={`${testId}-bar`}
        />
      ) : null}
      <SkyText size={200} color="muted" testId={`${testId}-line`}>
        {line}
      </SkyText>
      {speaks ? <LiveLine text={announcement} testId={`${testId}-live`} /> : null}
    </div>
  );
}

/** The editor card's own body (`bare`): the bar, the line, the cause and the steps. */
function barePanelView(view: StripView): ReactElement {
  const { activity, testId, live, progress, barLabel, line, speaks, announcement, stepper } = view;
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      {live ? (
        <SkyProgressBar
          thickness="medium"
          value={progress}
          label={barLabel}
          testId={`${testId}-bar`}
        />
      ) : null}
      <SkyText size={200} block testId={`${testId}-line`}>
        {line}
      </SkyText>
      <LivenessText text={view.liveness} testId={testId} />
      {activity.phase === "failed" && activity.cause ? (
        <QuietMessageBar
          intent="error"
          layout="multiline"
          message={activity.cause}
          testId={`${testId}-cause`}
        />
      ) : null}
      {live && stepper.items.length > 0 ? (
        <NamedStepper
          stepper={stepper}
          orientation="vertical"
          showStepNumbers
          size="small"
          allowClickOnCompleted={false}
          testId={`${testId}-steps`}
        />
      ) : null}
      {speaks ? <LiveLine text={announcement} testId={`${testId}-live`} /> : null}
    </div>
  );
}

/** The panel card's title row: the title, the badge, and View import details. */
function panelHeader(view: StripView, title: string): ReactElement {
  const { activity, testId, connectionName, badge } = view;
  return (
    <div className="mb-2 flex flex-wrap items-center gap-3">
      <SkyText as="h3" size={300} weight="semibold">
        {title}
      </SkyText>
      {badge && activity.phase !== "never" ? (
        <SkyBadge appearance="tint" color={badge.color} testId={`${testId}-badge`}>
          {badge.label}
        </SkyBadge>
      ) : null}
      {activity.runId && activity.phase !== "never" && activity.phase !== "unavailable" ? (
        <span className="ml-auto">
          <ViewLink
            runId={activity.runId}
            connectionName={connectionName}
            testId={`${testId}-view`}
          >
            View import details
          </ViewLink>
        </span>
      ) : null}
    </div>
  );
}

/** The panel card's tables, folded under "Tables (n)". */
function tablesAccordion(
  testId: string,
  tables: ReactNode,
  tableCount: number | undefined,
): ReactElement {
  return (
    <div className="mt-3">
      <SkyAccordion testId={`${testId}-tables`}>
        <SkyAccordionItem
          value="tables"
          heading={tableCount !== undefined ? `Tables (${tableCount})` : "Tables"}
        >
          {tables}
        </SkyAccordionItem>
      </SkyAccordion>
    </div>
  );
}

/** A panel card: title, badge and View import details, then the bar, the line, cause, steps and tables. */
function panelView(
  view: StripView,
  neverAction: ReactNode,
  tables: ReactNode,
  tableCount: number | undefined,
): ReactElement {
  const { activity, testId, live, progress, barLabel, line, speaks, announcement, stepper } = view;
  const title = live ? "Import in progress" : "Last import";
  return (
    <SkyCard className="p-4" testId={testId}>
      {panelHeader(view, title)}

      {live ? (
        <SkyProgressBar
          thickness="medium"
          value={progress}
          label={barLabel}
          className="mb-2"
          testId={`${testId}-bar`}
        />
      ) : null}
      <SkyText size={200} color={live ? undefined : "muted"} block testId={`${testId}-line`}>
        {line}
      </SkyText>
      <LivenessText text={view.liveness} testId={testId} />
      {activity.phase === "never" && neverAction ? <div className="mt-2">{neverAction}</div> : null}

      {activity.phase === "failed" && activity.cause ? (
        <div className="mt-2">
          <QuietMessageBar
            intent="error"
            layout="multiline"
            // Verbatim: the runner already reduced this text (SEC-17).
            message={activity.cause}
            testId={`${testId}-cause`}
          />
        </div>
      ) : null}

      {live && stepper.items.length > 0 ? (
        <div className="mt-3">
          <NamedStepper
            stepper={stepper}
            orientation="vertical"
            showStepNumbers
            size="small"
            allowClickOnCompleted={false}
            testId={`${testId}-steps`}
          />
        </div>
      ) : null}

      {tables ? tablesAccordion(testId, tables, tableCount) : null}

      {speaks ? <LiveLine text={announcement} testId={`${testId}-live`} /> : null}
    </SkyCard>
  );
}

/** The run page: the line, the bar while live, every step. */
function pageView(view: StripView): ReactElement {
  const { testId, live, progress, barLabel, line, speaks, announcement, stepper } = view;
  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <SkyText size={300} weight="semibold" block testId={`${testId}-line`}>
        {line}
      </SkyText>
      <LivenessText text={view.liveness} testId={testId} />
      {live ? (
        <SkyProgressBar
          thickness="medium"
          value={progress}
          label={barLabel}
          testId={`${testId}-bar`}
        />
      ) : null}
      {stepper.items.length > 0 ? (
        <NamedStepper
          stepper={stepper}
          orientation="vertical"
          showStepNumbers
          size="medium"
          allowClickOnCompleted={false}
          testId={`${testId}-steps`}
        />
      ) : null}
      {speaks ? <LiveLine text={announcement} testId={`${testId}-live`} /> : null}
    </div>
  );
}

export function ImportProgress({
  activity,
  density,
  connectionName,
  announce,
  tables,
  tableCount,
  neverAction,
  bare = false,
  testId = "import-progress",
  now: nowProp,
}: ImportProgressProps) {
  const live = isActivityLive(activity);
  const zone = useDisplayTz();
  // #1260: the relative time moves while the page stays open, unless a caller pins `now`.
  // #2389: the liveness line's seconds move every 10 seconds while it shows.
  const ticked = useNow(
    showsLiveness(activity) && density !== "row" ? LIVENESS_TICK_MS : undefined,
  );
  const now = nowProp ?? ticked;
  const line = activityLine(activity, density, now, zone);
  const badge = badgeOf(activity.phase);
  const progress = activityProgress(activity);
  const barLabel = barLabelOf(activity, connectionName);
  const announcement = activityAnnouncement(activity, connectionName);
  const speaks = announce ?? density === "panel";
  const stepper = useMemo(() => stepItems(activity.steps), [activity.steps]);
  const liveness = density === "row" ? null : livenessLine(activity, now);

  if (activity.phase === "loading") {
    return (
      <SkySkeleton testId={`${testId}-loading`} aria-busy="true">
        <SkySkeletonItem shape="rectangle" width={density === "row" ? 160 : 320} size={12} />
      </SkySkeleton>
    );
  }

  const view: StripView = {
    activity,
    testId,
    connectionName,
    badge,
    live,
    progress,
    barLabel,
    line,
    speaks,
    announcement,
    stepper,
    liveness,
  };
  // Each density's element tree, called rather than rendered as a component, so the element a
  // strip returns is exactly the one it always returned.
  if (density === "row") return rowView(view);
  if (density === "panel" && bare) return barePanelView(view);
  if (density === "panel") return panelView(view, neverAction, tables, tableCount);
  return pageView(view);
}
